import { Flex, Text, Table, Code, Heading } from "@radix-ui/themes";
import { Button, DropdownMenu, IconButton } from "../elements.js";
import { DotsHorizontalIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import { RevokeApiKeyDialog } from "./revoke-api-key-dialog.js";
import { CreateApiKeyButton } from "./create-api-key.js";
import { ApiKeyDetailsDialog } from "./api-key-details-dialog.js";
import { useIsHydrated } from "../use-is-hydrated.js";
import { RelativeTime } from "./relative-time.js";
import {
  ListOrganizationApiKeysResponse,
  ListOrganizationApiKeysResponseData,
} from "../../api/endpoint.js";
import { useApiKeysContext } from "./api-keys-context.js";
import { useApiKeysSearchContext } from "./api-keys-search-provider.js";
import { ApiKeysSearch } from "./api-keys-search.js";
import { Translation } from "../i18n/translation.js";
import { useTranslation } from "../i18n/use-translation.js";
import { useLocale } from "../i18n/use-locale.js";
import { getListMetadata } from "../utils.js";

function NoApiKeys() {
  const { clearSearch } = useApiKeysSearchContext();
  return (
    <Flex p="6" gap="4" justify="center" align="center" direction="column">
      <Flex direction="column" gap="1" maxWidth="420px" align="center">
        <MagnifyingGlassIcon
          style={{ color: "var(--gray-9)" }}
          width="32px"
          height="32px"
        />
        <Heading size="5" mb="1" wrap="balance" as="h3">
          <Translation
            defaultMessage="No API keys match your search"
            id="cKitHb"
            description="Empty state message when no API keys match search"
          />
        </Heading>
      </Flex>

      <Button variant="secondary" onClick={clearSearch}>
        <Translation
          defaultMessage="Clear search"
          id="jTupOW"
          description="Button to clear API key search filters"
        />
      </Button>
    </Flex>
  );
}

function ApiKeysTableHeader() {
  return (
    <Flex justify="between" align="center">
      <ApiKeysSearch name="api-keys-widget-search" style={{ width: "390px" }} />
      <CreateApiKeyButton />
    </Flex>
  );
}

function CreatedOn({ createdOn }: { createdOn: Date }) {
  const isHydrated = useIsHydrated();
  const locale = useLocale();
  return (
    <>
      {createdOn.toLocaleDateString(locale, {
        timeZone: isHydrated ? undefined : "UTC",
        month: "short",
        day: "numeric",
        year: "numeric",
      })}
    </>
  );
}

interface ApiKeyRowProps {
  apiKey: ListOrganizationApiKeysResponseData;
}

function ApiKeyRow({ apiKey }: ApiKeyRowProps) {
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const translate = useTranslation();
  return (
    <>
      <RevokeApiKeyDialog
        // always remount the form when the dialog is opened to reset the validation errors
        key={`${revokeDialogOpen}`}
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        apiKey={apiKey}
      />
      <ApiKeyDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        apiKey={apiKey}
        permissions={apiKey.permissions}
      />
      <Table.Row align="center">
        <Table.Cell>
          <Text>{apiKey.name}</Text>
        </Table.Cell>
        <Table.Cell>
          <Code color="gray">{apiKey.obfuscatedValue}</Code>
        </Table.Cell>
        <Table.Cell>
          <Text
            style={apiKey.lastUsedAt ? undefined : { color: "var(--gray-10)" }}
          >
            <RelativeTime
              date={apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt) : undefined}
            />
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            <CreatedOn createdOn={new Date(apiKey.createdAt)} />
          </Text>
        </Table.Cell>
        <Table.Cell justify="end">
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              {(() => {
                const title = translate({
                  defaultMessage: "API key actions",
                  id: "RIBfYe",
                  description: "Tooltip for API key actions menu button",
                });
                return (
                  <IconButton title={title}>
                    <DotsHorizontalIcon />
                  </IconButton>
                );
              })()}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content size="2" align="end">
              <DropdownMenu.Item onSelect={() => setDetailsOpen(true)}>
                <Translation
                  defaultMessage="View details"
                  id="VGKbbj"
                  description="Menu item to view API key details"
                />
              </DropdownMenu.Item>
              <DropdownMenu.Item
                variant="destructive"
                onSelect={() => setRevokeDialogOpen(true)}
              >
                <Translation
                  defaultMessage="Revoke key"
                  id="KZIJPP"
                  description="Menu item to revoke an API key"
                />
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Table.Cell>
      </Table.Row>
    </>
  );
}

export function ApiKeysTable({
  apiKeys,
}: {
  apiKeys: ListOrganizationApiKeysResponse;
}) {
  const { dispatch } = useApiKeysContext();

  // The API response previously returned an object with the `listMetadata`
  // property. We might not need this, but it's a defensive check until we
  // verify the API response changes are correct.
  const pagination = getListMetadata(apiKeys);

  const hasPagination = !!(pagination.before || pagination.after);
  const hasPreviousPage = !!pagination.after;
  const hasNextPage = !!pagination.before;

  return (
    <Flex direction="column" align="stretch" gap="3">
      <ApiKeysTableHeader />
      {apiKeys.data.length === 0 ? (
        <NoApiKeys />
      ) : (
        <>
          <Table.Root variant="ghost" size="2">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell width="25%">
                  <Translation
                    defaultMessage="Name"
                    id="VYbVXN"
                    description="Table column header for API key name"
                  />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="25%">
                  <Translation
                    defaultMessage="Secret key"
                    id="H5k7GB"
                    description="Table column header for API key secret value"
                  />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="25%">
                  <Translation
                    defaultMessage="Last used"
                    id="E46t3O"
                    description="Table column header for API key last used timestamp"
                  />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="25%">
                  <Translation
                    defaultMessage="Created"
                    id="CXHhXd"
                    description="Table column header for API key creation date"
                  />
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="28px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {apiKeys.data.map((apiKey) => (
                <ApiKeyRow key={apiKey.id} apiKey={apiKey} />
              ))}
            </Table.Body>
          </Table.Root>
          {hasPagination && (
            <Flex gap="2" justify="end">
              <Button
                variant="secondary"
                disabled={!hasPreviousPage}
                size="1"
                onClick={() =>
                  dispatch({
                    type: "SET_PAGINATION",
                    pagination: { before: null, after: pagination.after },
                  })
                }
              >
                <Translation
                  defaultMessage="Previous"
                  id="f7TUj0"
                  description="Button to navigate to previous page of API keys"
                />
              </Button>
              <Button
                variant="secondary"
                disabled={!hasNextPage}
                size="1"
                onClick={() =>
                  dispatch({
                    type: "SET_PAGINATION",
                    pagination: { before: pagination.before, after: null },
                  })
                }
              >
                <Translation
                  defaultMessage="Next"
                  id="L2EBxV"
                  description="Button to navigate to next page of API keys"
                />
              </Button>
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}
